import type { AIBackgroundPromptPayload, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { LayerData } from './components/Layer.types';
import type { LocationState } from 'history';

export type PrintDesignNavigationStep = 'cardFront' | 'cardBack' | 'envelope';
export type DigitalDesignNavigationStep = 'cardFront' | 'cardBack' | 'envelope';
export type DesignNavigationStep = PrintDesignNavigationStep | DigitalDesignNavigationStep;

export type PrintNavigationStep = PrintDesignNavigationStep | 'recipients' | 'postcardRecipients' | 'review' | 'postCheckout';
export type DigitalNavigationStep = DigitalDesignNavigationStep | 'review' | 'postCheckout' | 'success';

export type NavigationStep = PrintNavigationStep | DigitalNavigationStep;

export interface NavigationContext<T extends string | number | symbol> {
  currentStep: T;
  steps: Record<
    T,
    {
      /**
       * A simple boolean that reflects whether a user has gone through this step.
       * The step component itself should employ business logic to determine if the step was actually completed.
       */
      hasCompleted: boolean;
    }
  >;

  navigateForward: () => void;

  goToStep: (step: T, state?: LocationState) => void;

  draftId: string;
}

/**
 * Not finalized data structure
 */
export type DraftData = {
  config: ThemeJson;
  template: {
    name?: string | null | undefined;
    themeId: string;
  };
  stationeryTemplateCategory: StationeryTemplateCategoryEnum;
};

export type Envelope = {
  layers: Array<LayerData>;
  layout: CardEnvelopeReturnAddressLayout;
  recipientAddressLayout?: CardEnvelopeRecipientAddressLayout;
  recipientAddressSelection?: CardEnvelopeRecipientAddressSelection;
  recipientAddressLayers?: Array<LayerData>;
  recipientIdList?: Array<string>;
  recipientIdWithAddressListLength?: number;
  /**
   * Stores the envelope template themeId (e.g. 'traditional_envelope', 'bohemian_eucalyptus').
   * Legacy drafts may hold a CardEnvelopeDesign enum value ('traditional'|'script'|'modern') —
   * normalise with LEGACY_DESIGN_TO_THEME_ID before use.
   */
  design: string;
  showCountryInReturnAddress?: boolean;
};

export interface PageDimensions {
  readonly width: number;
  readonly height: number;
  readonly cutMargin: number;
  readonly safeZoneMargin: number;
  readonly defaultCut?: CardDieCut;
}

export type ColorwayOption = Readonly<{ id: string; values: string[] }>;

export type Colorways = Readonly<{
  selectedId?: string;
  options: ReadonlyArray<ColorwayOption>;
}>;

export interface PageJson {
  dimensions?: PageDimensions;
  fill: string;
  layers: Array<LayerData>;
  colorways?: Colorways;
}

export type CardRecipientAddressMode = 'now' | 'later' | 'csv' | 'none';

// Immer-mutated card JSON (like `PageJson`), so fields are writable. `layers` come from the back
// template; `mode`/`ids`/`showCountryInReturnAddress` are filled in by the PostcardRecipients step,
// so they are absent on a freshly-templated draft.
export type Recipients = {
  layers: Array<LayerData>;
  mode?: CardRecipientAddressMode;
  ids?: Array<string>;
  showCountryInReturnAddress?: boolean;
};

// `recipients` is a writable property to match `PageJson`'s other fields: this is the
// immer-mutated card JSON. Postcard recipient state (mode, ids, showCountryInReturnAddress) is
// written to `card.back.recipients` by PostcardRecipientsWrapper. The template layers are also
// seeded into `card.back.recipients.layers` by setTemplate (copied from the template's card.front,
// which is where template authors put the back design per the side:'back' convention).
export type BackPageJson = PageJson & {
  recipients?: Recipients;
  /**
   * "Match front colors" is on for this back: its fill and ink carry the colors read off the
   * card front's selected colorway. Editor state only — the colors themselves are written into
   * `fill` / `textData.fill`, so no renderer needs to know this flag exists. Absent means off,
   * which is the default for new and existing drafts alike. See `utils/frontColorsOnBack`.
   */
  useFrontColors?: boolean;
  /** The back's own colors, kept so the flag can be turned off again. Absent whenever the flag is. */
  frontColorsBackup?: FrontColorsBackup;
};

/**
 * A back's original colors, captured before the front's are written over them.
 *
 * `inks` runs parallel to `layers` so a restore can put each color back where it came from, with
 * `null` for the layers that carry no ink (images, rectangles, foil). It is re-captured whenever
 * the back layout changes, so it always describes the layers currently in the draft.
 */
export type FrontColorsBackup = {
  fill?: string;
  inks: Array<string | null>;
};

export interface ThemeJson {
  name: string;
  $schema: string;
  customizations: Pick<CardPriceModifiers, 'size' | 'paperType' | 'shape' | 'quantity' | 'delivery' | 'promoCode'> & {
    returnAddress?: CardAddress;
    deliveryAddress?: CardAddress;
    billingAddress?: CardAddress;
  };
  digitalCustomizations?: DigitalCardPriceModifiers & {
    backgroundColor?: string;
    envelopeColor?: string;
    envelopeAnimation?: string;

    // As of 2026 Apr, interactive=false also disables textures and gradients. For customers that want exact colors.
    interactive?: boolean; // Default true.

    // Legacy: a stamp free Basic Digital V2 drafts used to write here, back when they were
    // created without a `priceConfigId` and so had no product to read. Nothing writes or
    // reads it any more -- the draft's own `product.productType` is the source of truth --
    // but it is still present in blobs persisted before that changed. Do not revive it.
    productType?: DigitalProductType;
  };
  filledAt?: string;
  productMetadata?: {
    selectedAttributes: {
      attributeId: string;
      optionId: string;
    }[];
  };
  card: {
    /**
     * Do not read this property directly. Instead use `getDimensions()` (see Card.utils.ts)
     */
    layout: PageDimensions;
    front: PageJson;
    back?: BackPageJson & { layout: string };
    envelope: Envelope;
  };
  imageHistory: Array<ImageHistoryElement>;
}

export interface ImageHistoryElement {
  type: string;
  url: string;
}

export interface ImageHistoryUpload extends ImageHistoryElement {
  type: 'upload';
}

export interface ImageHistoryVariation extends ImageHistoryElement {
  type: 'ai_prompt';
  promptPayload: AIBackgroundPromptPayload;
  baseUrl: string | null;
  resourceID: string | null;
}

export enum CardSize {
  classic = 'classic',
}

export enum CardPaperType {
  signature = 'standard',
  smooth = 'smooth',
  semiGloss = 'semiGloss',
  pearlescent = 'pearlescent',
  doubleThick = 'doubleThick',
  tripleThick = 'tripleThick',
}

export enum CardDieCut {
  rounded = 'rounded',
  rectangle = 'rectangle',
  arch = 'arch',
}

export enum CardDelivery {
  economy = 'economy',
  standard = 'standard',
  express = 'express',
  upsGroundCanadaStandard = 'ups_ground_canada_standard',
  upsCanadaExpressSaver = 'ups_canada_express_saver',
  upsInternationalExpressSaver = 'ups_international_express_saver',
}

export enum CardEnvelopeReturnAddressLayout {
  front = 'front',
  back = 'back',
  none = 'none',
}

export enum CardEnvelopeRecipientAddressLayout {
  front = 'front',
  none = 'none',
}

export enum CardEnvelopeRecipientAddressSelection {
  later = 'later',
  csv = 'csv',
  now = 'now',
}

export enum CardEnvelopeDesign {
  traditional = 'traditional',
  script = 'script',
  modern = 'modern',
}

export interface PriceConfig {
  quantity: {
    [Key in StationeryTemplateCategoryEnum]: Record<
      number,
      {
        quantity: number;
        totalPriceInMinorUnits: number;
        individualPriceInMinorUnits: number;
        individualPriceString: string;
        totalPriceString: string;
      }
    >;
  };
  shape: Record<
    CardDieCut,
    {
      code: string | undefined;
      label: string;
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string | undefined;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
  paperType: {
    [Key in CardPaperType]: Record<
      number,
      {
        quantity: number;
        totalPriceInMinorUnits: number;
        individualPriceString: string;
        totalPriceString: string;
      }
    >;
  };
  delivery: Record<CardDelivery, number>;
  returnAddress: Record<
    CardEnvelopeReturnAddressLayout,
    {
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
  recipientAddress?: Record<
    CardEnvelopeRecipientAddressLayout,
    {
      individualPriceString: string | undefined;
      getTotalPriceStringByQuantity: (quantity: number) => string;
      getTotalPriceByQuantityInMinorUnits: (quantity: number) => number;
    }
  >;
}

export interface CardPriceModifiers {
  quantity: number;
  size: CardSize;
  paperType: CardPaperType;
  shape: CardDieCut;
  backLayout: string;
  returnAddress: CardEnvelopeReturnAddressLayout;
  recipientAddress?: CardEnvelopeRecipientAddressLayout;
  delivery?: CardDelivery;
  // Only storing the promo code value and not the config
  promoCode?: string;
  foilData?: { colorId: string; assetId: string };
}

export type DeliveryPackageKey = 'basic' | 'premium' | 'diamond';

/**
 * The digital products a draft can be for. Matches the `productType` values in
 * `useProductTypes` -- `digitalCard` is paid premium digital, `basicDigitalV2` is free.
 */
export type DigitalProductType = 'digitalCard' | 'basicDigitalV2';

export const DeliveryFeatures = {
  email: (quantity?: number) => `email${quantity ? `_${quantity}` : ''}`,
  sms: () => `sms`,
  international_sms: (quantity?: number) => `internationalsms${quantity ? `_${quantity}` : ''}`,
  link: () => `link`,
};

export interface DigitalCardPriceModifiers {
  // deliveryFeatures?: DigitalDeliveryFeatures[];
  quantity?: string;
  deliveryPackageKey?: DeliveryPackageKey;
}

export interface CardAddress {
  name: string;
  address1: string;
  address2: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  countryCode: string;
  phoneNumber?: string;
}
